import type { AgentState, AgentView, MessageKind, MessageView } from "@walkie/protocol"
import {
	Fragment,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { agentColor, agentTint, STATE_LABEL } from "../lib/agents"
import { refUrl } from "../lib/refs"
import { focusAgent } from "../store/actions"
import { useAgentsByHandle, useGlyphs } from "../store/derived"
import { useStore } from "../store/store"

// --- tooltip -------------------------------------------------------------------

/** Hover/focus tooltip. The anchor's own aria-label carries the same facts for screen readers. */
export function Tip(props: {
	content: ReactNode | (() => ReactNode)
	children: ReactNode
	className?: string
}) {
	const anchor = useRef<HTMLSpanElement>(null)
	const tip = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const show = () => {
		if (timer.current) clearTimeout(timer.current)
		timer.current = setTimeout(() => setOpen(true), 180)
	}
	const hide = () => {
		if (timer.current) clearTimeout(timer.current)
		setOpen(false)
		setPos(null)
	}
	useEffect(() => () => void (timer.current && clearTimeout(timer.current)), [])

	useLayoutEffect(() => {
		if (!open || !anchor.current || !tip.current) return
		const a = anchor.current.getBoundingClientRect()
		const t = tip.current.getBoundingClientRect()
		let x = a.left + a.width / 2 - t.width / 2
		x = Math.max(8, Math.min(window.innerWidth - t.width - 8, x))
		let y = a.bottom + 6
		if (y + t.height > window.innerHeight - 8) y = a.top - t.height - 6
		setPos({ x, y })
	}, [open])

	return (
		<span
			ref={anchor}
			className={"tip-anchor " + (props.className ?? "")}
			onMouseEnter={show}
			onMouseLeave={hide}
			onFocus={show}
			onBlur={hide}
		>
			{props.children}
			{open &&
				createPortal(
					<div
						ref={tip}
						className="tip"
						role="tooltip"
						style={{ left: pos?.x ?? -9999, top: pos?.y ?? -9999 }}
					>
						{typeof props.content === "function" ? props.content() : props.content}
					</div>,
					document.body,
				)}
		</span>
	)
}

// --- agents & people -----------------------------------------------------------------

export function Swatch({ agent, size }: { agent: AgentView; size?: "lg" }) {
	const glyphs = useGlyphs()
	return (
		<span
			className={"swatch" + (size ? " " + size : "")}
			style={{
				color: agentColor(agent.color),
				background: agentTint(agent.color, 0.16),
				borderColor: agentTint(agent.color, 0.5),
			}}
			aria-hidden
		>
			{glyphs.get(agent.id) ?? agent.handle[0]?.toUpperCase()}
		</span>
	)
}

export function PersonSwatch({ handle, size }: { handle: string; size?: "lg" }) {
	return (
		<span className={"swatch person" + (size ? " " + size : "")} aria-hidden>
			{handle[0]?.toUpperCase() ?? "?"}
		</span>
	)
}

/** Swatch + @handle for any message author. */
export function Author({ m }: { m: MessageView }) {
	const agent = useStore(s =>
		m.author.type === "agent" && m.author.id ? s.agents[m.author.id] : undefined,
	)
	if (agent)
		return (
			<>
				<Swatch agent={agent} />
				<button
					className="who"
					style={{ color: agentColor(agent.color) }}
					onClick={() => focusAgent(agent.id)}
					aria-label={`@${agent.handle}, agent of ${agent.ownerHandle}; show in agents panel`}
				>
					@{agent.handle}
				</button>
			</>
		)
	if (m.author.type === "system" || m.author.type === "github")
		return (
			<>
				<span className="swatch sys" aria-hidden>
					{m.author.type === "github" ? "G" : "·"}
				</span>
				<span className="who dim2">{m.author.handle || m.author.type}</span>
			</>
		)
	return (
		<>
			<PersonSwatch handle={m.author.handle} />
			<span className="who">{m.author.handle}</span>
		</>
	)
}

export function HandleText({ id, handle }: { id?: string | null; handle?: string }) {
	const agent = useStore(s => (id ? s.agents[id] : undefined))
	const people = useStore(s => s.people)
	if (agent)
		return (
			<span style={{ color: agentColor(agent.color), fontWeight: 600 }}>
				@{agent.handle}
			</span>
		)
	const person = id ? people.find(p => p.userId === id) : undefined
	return (
		<span style={{ fontWeight: 600 }}>
			{person ? person.handle : (handle ?? "someone")}
		</span>
	)
}

export function StateDot({ state }: { state: AgentState }) {
	return (
		<span className={"state " + state}>
			<span className={"sdot s-" + state} aria-hidden />
			{STATE_LABEL[state]}
		</span>
	)
}

const KIND_LABEL: Partial<Record<MessageKind, string>> = {
	question: "question",
	blocker: "blocker",
	handoff: "handoff",
	decision: "decision",
	heads_up: "heads-up",
	event: "event",
	notice: "notice",
}

export function KindBadge({ kind }: { kind: MessageKind }) {
	const label = KIND_LABEL[kind]
	if (!label) return null
	return <span className={"kind k-" + kind}>{label}</span>
}

export function Ref({ value }: { value: string }) {
	const url = refUrl(value)
	return url ? (
		<a className="ref" href={url} target="_blank" rel="noreferrer noopener">
			{value}
		</a>
	) : (
		<span className="ref">{value}</span>
	)
}

// --- rich body -------------------------------------------------------------------------

const TOKEN =
	/(`[^`\n]+`|@[a-z0-9][a-z0-9-]{0,31}|[\w.-]+\/[\w.-]+#\d+|https?:\/\/[^\s)]+)/gi

export function Body({ text }: { text: string }) {
	const byHandle = useAgentsByHandle()
	const myHandle = useStore(s => s.me?.user.handle)
	const parts = text.split(TOKEN)
	return (
		<>
			{parts.map((p, i) => {
				if (i % 2 === 0) return <Fragment key={i}>{p}</Fragment>
				if (p.startsWith("`"))
					return (
						<code key={i} className="code">
							{p.slice(1, -1)}
						</code>
					)
				if (p.startsWith("@")) {
					const h = p.slice(1).toLowerCase()
					const a = byHandle.get(h)
					if (a)
						return (
							<span key={i} className="mention" style={{ color: agentColor(a.color) }}>
								{p}
							</span>
						)
					return (
						<span key={i} className={"mention" + (h === myHandle ? " me" : "")}>
							{p}
						</span>
					)
				}
				const url = refUrl(p)
				return url ? (
					<a
						key={i}
						href={url}
						target="_blank"
						rel="noreferrer noopener"
						className="dim2"
					>
						{p}
					</a>
				) : (
					<Fragment key={i}>{p}</Fragment>
				)
			})}
		</>
	)
}

// --- popover menu ----------------------------------------------------------------------

export function Popover(props: {
	anchor: HTMLElement | null
	onClose: () => void
	children: ReactNode
	align?: "left" | "right"
	label: string
}) {
	const ref = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState<{
		left?: number
		right?: number
		top?: number
		bottom?: number
	}>({
		top: -9999,
		left: -9999,
	})
	useLayoutEffect(() => {
		if (!props.anchor) return
		const r = props.anchor.getBoundingClientRect()
		// Open upward (anchored by its bottom edge) when there is no room below.
		const h = ref.current?.offsetHeight ?? 0
		const w = ref.current?.offsetWidth ?? 0
		const up = r.bottom + 4 + h > window.innerHeight - 8 && r.top - 4 - h >= 8
		const v = up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }
		setPos(
			props.align === "right"
				? { right: window.innerWidth - r.right, ...v }
				: { left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)), ...v },
		)
		ref.current?.querySelector<HTMLElement>("button, input, [tabindex]")?.focus()
	}, [props.anchor, props.align])
	useEffect(() => {
		const onDown = (e: MouseEvent) => {
			if (
				ref.current?.contains(e.target as Node) ||
				props.anchor?.contains(e.target as Node)
			)
				return
			props.onClose()
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation()
				props.onClose()
				props.anchor?.focus()
			}
			if (e.key === "ArrowDown" || e.key === "ArrowUp") {
				const items = [
					...(ref.current?.querySelectorAll<HTMLElement>(".menu-item") ?? []),
				]
				const i = items.indexOf(document.activeElement as HTMLElement)
				const next =
					items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]
				next?.focus()
				e.preventDefault()
			}
		}
		document.addEventListener("mousedown", onDown)
		document.addEventListener("keydown", onKey, true)
		return () => {
			document.removeEventListener("mousedown", onDown)
			document.removeEventListener("keydown", onKey, true)
		}
	}, [props])
	return createPortal(
		<div ref={ref} className="menu" role="menu" aria-label={props.label} style={pos}>
			{props.children}
		</div>,
		document.body,
	)
}

/** Esc closes; focus returns to whatever had it before. */
export function useDismiss(onClose: () => void) {
	useEffect(() => {
		const prev = document.activeElement as HTMLElement | null
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation()
				onClose()
			}
		}
		document.addEventListener("keydown", onKey, true)
		return () => {
			document.removeEventListener("keydown", onKey, true)
			if (prev && document.contains(prev)) prev.focus({ preventScroll: true })
		}
	}, [onClose])
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
	const [done, setDone] = useState(false)
	return (
		<button
			className="btn btn-xs"
			onClick={() => {
				void navigator.clipboard.writeText(text).then(() => {
					setDone(true)
					setTimeout(() => setDone(false), 1500)
				})
			}}
		>
			{done ? "Copied" : label}
		</button>
	)
}
